import React, { useEffect, useState } from 'react';
import CategoryGroup from './CategoryGroup';
import styles from './CategoryNav.module.scss';

/**
 * Category navigation component
 * Displays product categories as hierarchical accordion (Level 1 = headers, Level 2 = checkboxes)
 *
 * @param {Object} props
 * @param {Array} props.selectedCategories - Array of selected category slugs (Level 2 only)
 * @param {Array} props.availableCategories - Array of category slugs that have matching products (optional)
 * @param {Function} props.onChange - Callback when category selection changes
 */
export default function CategoryNav({ selectedCategories = [], availableCategories = null, onChange }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch categories on mount
    useEffect(() => {
        async function fetchCategories() {
            try {
                const response = await fetch('/intranet/produkte/categories');
                if (!response.ok) {
                    throw new Error('Failed to fetch categories');
                }
                const data = await response.json();
                setCategories(data);
            } catch (err) {
                console.error('Error fetching categories:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        fetchCategories();
    }, []);

    const handleCategoryChange = (categorySlug, isSelected) => {
        const newSelected = isSelected
            ? [...selectedCategories, categorySlug]
            : selectedCategories.filter(s => s !== categorySlug);

        onChange(newSelected);
    };

    if (loading) {
        return (
            <div className={styles.categoryNav}>
                <div className={styles.loading}>Kategorien werden geladen...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.categoryNav}>
                <div className={styles.error}>Fehler beim Laden der Kategorien</div>
            </div>
        );
    }

    if (categories.length === 0) {
        return null;
    }

    return (
        <div className={styles.categoryNav}>
            <h2>Kategorien</h2>
            <ul className={styles.categoryList}>
                {categories.map((parentCategory) => (
                    <CategoryGroup
                        key={parentCategory.slug}
                        title={parentCategory.title}
                        children={parentCategory.children}
                        selectedCategories={selectedCategories}
                        availableCategories={availableCategories}
                        onChange={handleCategoryChange}
                    />
                ))}
            </ul>
        </div>
    );
}
